import tenantConfig from '../../tenant.config.json';

export type CrmType = 'hubspot' | 'attio' | null;

export interface TenantConfig {
  tenant: {
    slug: string;
    name: string;
    domain: string;
    adminDomain: string;
    logo: string;
    primaryColor: string;
    supportEmail: string;
  };
  crm: {
    type: CrmType;
    fieldMap: {
      region: string | null;
      verdict: string | null;
      inspectorId: string | null;
    };
  };
  regions: {
    preset: string;
    customCenters: { lat: number; lng: number; label: string; radiusKm: number }[];
  };
  features: {
    scheduling: boolean;
    displayMode: boolean;
    brainAi: boolean;
    sheetsBinding: boolean;
  };
  version: number;
}

/**
 * Resolved tenant — populated at app boot by TenantProvider from `v9_tenants`,
 * keyed off `VITE_TENANT_SLUG` (env) which falls back to `tenant.config.json`'s
 * `tenant.slug`. The DB row is the source of truth for the UUID + status.
 */
export interface ResolvedTenant {
  id: string;
  slug: string;
  name: string;
  primary_domain: string;
  admin_domain: string;
  isolation_mode: 'master' | 'forked';
  status: 'active' | 'trial' | 'paused' | 'archived';
  branding: Record<string, unknown>;
  features: Record<string, unknown>;
}

export const tenant = tenantConfig as TenantConfig;

export function getTenant(): TenantConfig {
  return tenant;
}

/**
 * Slug used to look up the tenant row in `v9_tenants`.
 *
 * Resolution order:
 *   1. `VITE_TENANT_SLUG` env (per-client forks bake this in at build-time)
 *   2. `?tenant=` query param (fallback for previewing on netlify.app while
 *      the production CNAMEs are still pending)
 *   3. Hostname pattern `admin.demo-{trade}.aidxn.com` → `demo-{trade}`
 *      (also accepts a bare `demo-{trade}.aidxn.com` shape)
 *   4. `tenant.config.json` fallback (local dev: `velocity9-dev`)
 *
 * Case-insensitive throughout. Hostname check ignores the port.
 */
export function getTenantSlug(): string {
  const envSlug = (import.meta.env.VITE_TENANT_SLUG as string | undefined)?.trim();
  if (envSlug) return envSlug;

  if (typeof window === 'undefined') {
    return tenant.tenant.slug;
  }

  // 2. ?tenant= query param wins over hostname so we can preview any tenant
  //    on the shared netlify.app subdomain while DNS is pending.
  try {
    const qp = new URLSearchParams(window.location.search).get('tenant')?.trim().toLowerCase();
    if (qp && /^[a-z0-9-]+$/.test(qp)) return qp;
  } catch {
    /* ignore — non-browser context */
  }

  // 3. Hostname-based resolution. `window.location.hostname` already strips
  //    the port. Lowercase + match the canonical demo domain shapes.
  const host = (window.location.hostname || '').toLowerCase();

  // 3a. New canonical demo shape: `{niche}-admin-demo.aidxn.com` → slug `demo-{niche}`.
  //     Niche reads first (better SEO, cleaner branding) while tenant slugs in
  //     the DB stay `demo-{niche}` so we don't churn FK relationships.
  const nicheMatch = host.match(/^([a-z0-9-]+?)-admin-demo\.aidxn\.com$/);
  if (nicheMatch) return `demo-${nicheMatch[1]}`;

  // 3b. Legacy shape: `admin.demo-{trade}.aidxn.com` → slug `demo-{trade}`.
  //     Kept for backwards compat during the DNS migration window — drop once
  //     all demo rows in v9_tenants.admin_domain reference the new shape.
  const demoMatch = host.match(/^admin\.(demo-[a-z0-9-]+)\.aidxn\.com$/);
  if (demoMatch) return demoMatch[1];

  // 3c. Also support a bare `demo-{trade}.aidxn.com` shape (apex demo subdomain
  //     pointed straight at this site without any prefix).
  const bareMatch = host.match(/^(demo-[a-z0-9-]+)\.aidxn\.com$/);
  if (bareMatch) return bareMatch[1];

  // 4. Local dev / preview deploys / anything else → file fallback.
  return tenant.tenant.slug;
}
